import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

export type AccountArea = 'influencer' | 'brand' | 'talent-partner' | 'agency';

/**
 * Which account types each area (/influencer/*, /brand/*, /talent-partner/*,
 * /agency/*) belongs to. Brand screens are not for influencers and influencer
 * screens are not for brands; only pages genuinely used by both (Zap
 * Automations, Link in Bio, Storefront) are mounted once per area, so a URL's
 * area always says whose page it is.
 *
 * - Talent Partners work as their creators in /influencer (switchuser.tsx).
 * - Agencies work as their brands in /brand.
 */
const AREA_TYPES: Record<AccountArea, string[]> = {
  influencer: ['influencer', 'talent partner'],
  brand: ['brand', 'agency'],
  'talent-partner': ['talent partner'],
  agency: ['agency'],
};

/**
 * Pages inside an area that other account types legitimately open: the admin
 * dashboard is under /brand but admins of any account type reach it from the
 * header (data.js InfluencerHeaderMenu id 6); its own pages check permission.
 */
const COMMON_PREFIXES = ['/brand/admin-dashboard'];

const under = (pathname: string, prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Where someone who does not belong in an area goes instead (header.tsx getDashboardLink). */
const homeFor = (types: string[]) => {
  if (types.includes('agency') || types.includes('talent partner')) return '/switch/user';
  if (types.includes('brand')) return '/brand/home';
  if (types.includes('influencer')) return '/influencer';
  return '/login';
};

/**
 * Keeps an area to its account types. Both the signed-in account
 * (UserReducer.UserData) and the owning account (SwitchLayer2Reducer.switchUser)
 * count, since an agency acting as a brand / a Talent Partner acting as a creator
 * merges the picked row into UserData.
 *
 * Permissive while the account type is unknown: guests (an emailed
 * /influencer/campaign?key= link renders without login, see protected-route.tsx)
 * and a session still restoring are never redirected.
 */
export const AccountAreaGuard: React.FC<{ area: AccountArea; children: React.ReactNode }> = ({ area, children }) => {
  const { pathname } = useLocation();
  const user = useSelector((state: any) => state?.UserReducer?.UserData);
  const owner = useSelector((state: any) => state?.SwitchLayer2Reducer?.switchUser);

  const types = [user?.account_type, owner?.account_type]
    .map((t) => String(t || '').trim().toLowerCase())
    .filter(Boolean);
  if (!types.length) return <>{children}</>;
  if (COMMON_PREFIXES.some((p) => under(pathname, p))) return <>{children}</>;
  if (types.some((t) => AREA_TYPES[area].includes(t))) return <>{children}</>;

  return <Navigate to={homeFor(types)} replace />;
};
